import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import SEOHead from "../components/SEOHead";
import { useLanguage } from "../context/LanguageContext";
import { CHAPTERS, groupByRegion } from "../lib/chapters";

const RecherchePage = () => {
  const { lang } = useLanguage();
  const [query, setQuery] = useState("");
  const [region, setRegion] = useState("all");

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const grouped = groupByRegion();
  const regions = ["all", ...Object.keys(grouped)];

  const filtered = useMemo(() => {
    return CHAPTERS.filter((c) => {
      const regionOk = region === "all" || c.region === region;
      const q = query.trim().toLowerCase();
      const qOk = !q || c.title.toLowerCase().includes(q) || c.summary.toLowerCase().includes(q);
      return regionOk && qOk;
    });
  }, [query, region]);

  const isFr = lang === "fr";
  const seoTitle = isFr
    ? "Centre bibliographique IMOTEpS — chapitres académiques épaule & membre supérieur"
    : "IMOTEpS Bibliography Center — academic chapters on shoulder & upper limb";
  const seoDescription = isFr
    ? "12 chapitres académiques rédigés par le Dr Richard Aswad : capsulite, coiffe des rotateurs, instabilité, omarthrose, PRP, ondes de choc, tendinopathies, neurologie du membre supérieur, jeune sportif."
    : "12 academic chapters written by Dr Richard Aswad: frozen shoulder, rotator cuff, instability, glenohumeral osteoarthritis, PRP, shockwaves, tendinopathies, upper-limb neurology, young athlete.";

  return (
    <main data-testid="recherche-page" className="bg-white pt-28 md:pt-36 pb-20">
      <SEOHead
        title={seoTitle}
        description={seoDescription}
        canonical="/recherche"
        keywords="centre bibliographique orthopédie, bibliographie épaule Marseille, articles scientifiques épaule, Dr Aswad publications, IMOTEpS bibliographie"
        lang={lang}
      />
      <div className="container-page">
        <Link to="/" className="inline-flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-[#FF5A1F]">
          <i className="fa-solid fa-arrow-left" aria-hidden="true"></i>
          {lang === "fr" ? "Retour à l'accueil" : "Back to home"}
        </Link>

        <div className="mt-6 max-w-3xl">
          <span className="chip mb-5">
            {lang === "fr" ? "Centre bibliographique" : "Bibliography center"}
          </span>
          <h1 className="font-display font-black text-5xl sm:text-6xl text-navy leading-[1.02]">
            {lang === "fr" ? "Centre bibliographique IMOTEpS" : "IMOTEpS Bibliography Center"}
          </h1>
          <p className="mt-5 text-base md:text-lg text-foreground/75 leading-relaxed">
            {lang === "fr"
              ? "Bibliothèque scientifique du Dr Richard Aswad. Synthèses didactiques, états de l'art et observations cliniques sur la chirurgie du membre supérieur. Chaque chapitre est conçu comme un véritable papier scientifique : références cliquables vers PubMed, mode lecture, export BibTeX et impression optimisée."
              : "Dr Richard Aswad's scientific library. Didactic syntheses, state-of-the-art reviews and clinical observations on upper-limb surgery. Each chapter is designed as a genuine scientific paper: clickable PubMed references, reading mode, BibTeX export and print-optimized layout."}
          </p>
        </div>

        {/* Filters */}
        <div className="mt-10 rounded-2xl border border-border bg-[#F8FAFC] p-5">
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            <div className="flex-1">
              <label className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
                {lang === "fr" ? "Recherche" : "Search"}
              </label>
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                data-testid="recherche-search"
                placeholder={lang === "fr" ? "Titre, mot-clé…" : "Title, keyword…"}
                className="mt-2 w-full rounded-md border border-border bg-white px-4 py-2.5 text-sm focus:border-[#FF5A1F] focus:outline-none focus:ring-2 focus:ring-[#FF5A1F]/20"
              />
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
                {lang === "fr" ? "Région" : "Region"}
              </label>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {regions.map((r) => (
                  <button
                    key={r}
                    type="button"
                    onClick={() => setRegion(r)}
                    data-testid={`recherche-region-${r}`}
                    className={`rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-[0.1em] border transition ${
                      region === r ? "bg-[#0A192F] text-white border-[#0A192F]" : "bg-white text-navy border-border hover:border-[#FF5A1F]/40"
                    }`}
                  >
                    {r === "all" ? (lang === "fr" ? "Tout" : "All") : r}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <p className="mt-4 text-xs text-muted-foreground">
            {filtered.length} {lang === "fr" ? "chapitre(s)" : "chapter(s)"}
          </p>
        </div>

        {/* Chapter cards grid */}
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map((c) => (
            <Link
              key={c.id}
              to={`/recherche/chapitre-${c.id}-${c.slug}`}
              data-testid={`chapter-card-${c.id}`}
              className="group relative rounded-2xl border border-border bg-white overflow-hidden card-hover flex flex-col"
            >
              {/* Color band */}
              <div className="h-2 w-full" style={{ backgroundColor: c.color }} />
              <div className="p-6 flex-1 flex flex-col">
                <div className="flex items-start justify-between gap-3">
                  <span
                    className="font-display text-[11px] font-extrabold uppercase tracking-[0.22em]"
                    style={{ color: c.color }}
                  >
                    Chapitre {c.id}
                  </span>
                  <span className="text-[10px] text-muted-foreground">
                    {c.readingMin} {lang === "fr" ? "min" : "min"}
                  </span>
                </div>
                <h2
                  className="mt-4 font-display text-xl font-bold text-navy leading-tight"
                  style={{ borderBottomColor: c.color }}
                >
                  {c.title}
                </h2>
                <p className="mt-2 text-xs font-semibold uppercase tracking-[0.14em]" style={{ color: c.color }}>
                  {c.region}
                </p>
                <p className="mt-3 text-sm text-foreground/70 leading-relaxed line-clamp-4">
                  {c.summary}
                </p>
                <div className="mt-5 pt-4 border-t border-border flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">
                    {c.references?.length || 0} {lang === "fr" ? "réf." : "refs"}
                  </span>
                  <span className="text-sm font-semibold flex items-center gap-1.5 transition-colors" style={{ color: c.color }}>
                    {lang === "fr" ? "Lire" : "Read"}
                    <i className="fa-solid fa-arrow-right text-[11px]" aria-hidden="true"></i>
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>

        <p className="mt-10 text-xs text-muted-foreground italic">
          {lang === "fr"
            ? "Document scientifique destiné à l'information du grand public et des confrères. Ne se substitue pas à une consultation médicale individualisée."
            : "Scientific document for general public and peer information. Does not replace an individualized medical consultation."}
        </p>
      </div>
    </main>
  );
};

export default RecherchePage;
